import { CloseOutlined, LeftOutlined } from "@ant-design/icons";
import { DndContext } from "@dnd-kit/core";
import { Button, Tooltip } from "antd";
import Cookies from "js-cookie";
import { delay } from "lodash";
import moment from "moment-timezone";
import { useEffect, useRef, useState } from "react";
import { FiSend } from "react-icons/fi";
import { LuAlarmClock } from "react-icons/lu";
import { MdOutlineHowToVote } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import apiFactory from "../../../api";
import { MessageAvatar } from "../../../components/avatar/MessageAvatar";
import { ShowTimeOffline } from "../../../components/avatar/ShowTimeOffline";
import { GenerateMessageContent } from "../../../components/chat/MessageContent";
import { ChatScheduleModal } from "../../../components/modal/chat/ChatScheduleModal";
import CreateVoteModal from "../../../components/modal/chat/CreateVoteModal";
import StickerItem from "../../../components/moodPack/sticker/StickerItem";
import StickerTable from "../../../components/moodPack/sticker/StickerTable";
import { FilesPreview } from "../../../components/preview/FilesPreview";
import { ContentEditableRichInput } from "../../../components/richInput/ContentEditableRichInput";
import { FileSending } from "../../../components/select/FileSending";
import { conversationType, MESSAGE_STATUS } from "../../../config/Constant";
import { useChatContext } from "../../../context/ChatContext";
import { useMenuContext } from "../../../context/MenuContext";
import { useInfoUser } from "../../../store/UserStore";
import { onChangeUnreadConversation } from "../../../utils/Utils";
import { ChatPreviewLink } from "./ChatPreviewLink";
import { QuotationResponse } from "./Quotation";
import "./style.scss";

const fileStore = process.env.REACT_APP_FILE_STORE || "http://10.1.1.230:8000";

const VirtualRoom = () => {
  const navigate = useNavigate();
  const { user, languageMap, stickerKeywords } = useInfoUser();

  /** Context */
  const {
    selectedConversation,
    conversationList,
    handleUpdateConversationList,
    quotation,
    setQuotation,
    setKeydownMenuOn,
  } = useMenuContext();

  const {
    friend,
    messageList,
    formData,
    setFormData,
    sendFormData,
    onBackToConversations,
    setIsNewMessage,
    isViewTargetMessage,
    viewTargetMessage,
    messageRef,
    messageMapRef,
    typingUsers,
    pinnedMessages,
    isChatScheduleModal,
    setIsChatScheduleModal,
    setScheduleMessage,
    wasAtBottomMessageRef,
    setIsScrollToLastMessage,
    jumpToMessage,
    inputValue,
    setInputValue,
    setUnreadInDOM,
  } = useChatContext();

  const inputSendRef = useRef(null);
  const [isOpenModalVote, setIsOpenModalVote] = useState(false);
  const [showSticker, setShowSticker] = useState(false);
  const [stickerSuggestions, setStickerSuggestions] = useState([]);

  const observerRef = useRef(null);

  const headerRef = useRef(null);

  const scrollToBottom = async () => {
    if (isViewTargetMessage) {
      // initMessageList();
      setIsScrollToLastMessage(false);
    } else if (
      messageRef?.current?.scrollHeight > messageRef?.current?.clientHeight
    ) {
      setIsNewMessage(false);
      delay(() => {
        if (messageRef?.current)
          messageRef.current.scrollTop = messageRef.current.scrollHeight;
      }, 200);
    }
  };

  const onScrollMessageList = (event) => {
    const el = event.currentTarget;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 10;
    wasAtBottomMessageRef.current = atBottom;
    if (atBottom) {
      setIsScrollToLastMessage(false);
      setIsNewMessage(false);
    } else {
      setIsScrollToLastMessage(true);
    }
  };

  const fetchLink = async (extractedLink) => {
    if (!extractedLink) return;

    const result = await apiFactory.previewApi.getLinkPreview({
      url: extractedLink,
    });

    if (result?.status !== 200) {
      setFormData({
        ...formData,
        previewLink: {
          image: null,
          title: null,
          description: null,
          url: null,
        },
      });
    } else {
      setFormData({
        ...formData,
        previewLink: {
          ...result?.data,
        },
      });
    }
  };

  const removePreviewLink = () => {
    setFormData({
      ...formData,
      previewLink: {
        image: null,
        title: null,
        description: null,
        url: null,
      },
    });
  };

  const isNotShowAvatar = (prevMessage, currentMessage) => {
    if (
      !prevMessage ||
      !currentMessage ||
      moment(prevMessage?.createdAt).format("YYYY-MM-DD") !==
        moment(currentMessage?.createdAt).format("YYYY-MM-DD") ||
      ![
        MESSAGE_STATUS.TEXT,
        MESSAGE_STATUS.FILE,
        MESSAGE_STATUS.TEXT,
        MESSAGE_STATUS.STICKER,
        MESSAGE_STATUS.REMOVE_MESSAGE,
      ].includes(prevMessage?.contentType)
    )
      return false;

    return prevMessage?.senderId === currentMessage?.senderId;
  };

  const isNotShowTime = (nextMessage, currentMessage) => {
    if (
      !nextMessage ||
      !currentMessage ||
      moment(nextMessage?.createdAt).format("YYYY-MM-DD hh:mm") !==
        moment(currentMessage?.createdAt).format("YYYY-MM-DD hh:mm") ||
      ![
        MESSAGE_STATUS.TEXT,
        MESSAGE_STATUS.FILE,
        MESSAGE_STATUS.TEXT,
        MESSAGE_STATUS.STICKER,
        MESSAGE_STATUS.REMOVE_MESSAGE,
      ].includes(nextMessage?.contentType)
    )
      return false;

    return nextMessage?.senderId === currentMessage?.senderId;
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    const cleanedInputValue = inputValue
      ?.split("\n")
      ?.filter((line) => line.length > 0)
      ?.join("\n");
    setFormData({
      ...formData,
      text: cleanedInputValue,
      quotation,
      previewLink: {
        image: null,
        title: null,
        description: null,
        url: null,
      },
    });

    const userIds =
      user?.userId === friend?.userId
        ? [user?.userId]
        : [user?.userId, friend?.userId];

    const result = await apiFactory.conversationApi.createGroup({
      name: "",
      userIds: userIds,
      conversationType: conversationType.PERSONAL,
    });

    sendFormData({
      ...formData,
      text: cleanedInputValue,
      conversationId: result?.data?.conversationId,
      quotation,
    });
    setInputValue("");
    scrollToBottom();
    setQuotation(null);
    navigate(`/conversation/chat/${result?.data?.conversationId}`);
  };

  const onKeyDown = (e) => {
    // Prevent Enter during Korean/IME composition
    if (e.isComposing || e.keyCode === 229 || e.keyCode !== 13) return;

    if (!e.shiftKey) {
      sendMessage(e);
    } else if (wasAtBottomMessageRef.current) {
      // chỉ kéo xuống khi user đang ở đáy trước đó
      requestAnimationFrame(() => {
        if (messageRef.current) {
          messageRef.current.scrollTop = messageRef.current.scrollHeight;
        }
      });
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    // event.preventDefault();
    // if (formData?.files?.length > exceedFiles) {
    //   openNotificationError(
    //     languageMap?.["error.exceed.fileNumber"] ??
    //       "The number of files exceeds 24",
    //   );
    //   return;
    // }
    // const items = event.dataTransfer?.items;
    // if (items) {
    //   let hasFolder = false;
    //   for (let i = 0; i < items.length; i++) {
    //     const entry = items[i].webkitGetAsEntry?.();
    //     if (entry && entry.isDirectory) {
    //       hasFolder = true;
    //       break;
    //     }
    //   }
    //   if (hasFolder) {
    //     openNotificationError(
    //       languageMap?.["error.not.sendFolder"] ?? "You can not send folder",
    //     );
    //     return;
    //   }
    // }
    // const droppedFiles = Array.from(event.dataTransfer.files)?.filter(
    //   (file) => file?.size !== 0,
    // );
    // let totalSizeUpload = droppedFiles.reduce((sum, f) => sum + f.size, 0);
    // if (totalSizeUpload > MAX_FILE_SIZE) {
    //   openNotificationError("File size exceeds the 1gb limit");
    //   return;
    // }
    // const mediaFiles = [];
    // const encodePhotos = [];
    // const promises = [];
    // droppedFiles.forEach((file) => {
    //   file.uid = uuidv4();
    //   const promise = new Promise((resolve) => {
    //     const reader = new FileReader();
    //     reader.readAsDataURL(file);
    //     reader.onloadend = (e) => {
    //       const base64 = e?.target?.result;
    //       if (imagesTypes.includes(file?.type)) {
    //         const img = new Image();
    //         img.src = base64;
    //         img.onload = () => {
    //           file.width = img.width;
    //           file.height = img.height;
    //           resolve();
    //         };
    //       } else {
    //         mediaFiles.push(file);
    //         resolve();
    //       }
    //     };
    //   });
    //   promises.push(promise);
    // });
    // Promise.all(promises).then(() => {
    //   setFormData((prev) => ({
    //     ...prev,
    //     text: prev?.text,
    //     files: [...(prev?.files || []), ...droppedFiles],
    //     mediaFiles: [...(prev?.mediaFiles || []), ...mediaFiles],
    //     encodePhotos: [...(prev?.encodePhotos || []), ...encodePhotos],
    //   }));
    //   setUploadFiles([...uploadFiles, ...(mediaFiles || [])]);
    // });
  };

  const handleFileChange = (e) => {
    // if (formData?.files?.length === exceedFiles) {
    //   openNotificationError(
    //     languageMap?.["error.exceed.fileNumber"] ??
    //       "The number of files exceeds 24",
    //   );
    //   return;
    // }
    // const text = formData.text;
    // const clipboardFiles = Array.from(e.clipboardData.items)
    //   .filter((item) => item.kind === "file")
    //   .map((item) => {
    //     const file = item.getAsFile();
    //     file.uid = uuidv4();
    //     return file;
    //   });
    // if (clipboardFiles.length === 0) return;
    // e.preventDefault();
    // const filePromises = clipboardFiles.map((file) => {
    //   return new Promise((resolve) => {
    //     const reader = new FileReader();
    //     reader.readAsDataURL(file);
    //     reader.onloadend = (r) => {
    //       if (imagesTypes.includes(file.type)) {
    //         const img = new Image();
    //         img.src = r.target.result;
    //         img.onload = () => {
    //           resolve({
    //             type: "image",
    //             data: {
    //               name: file.name,
    //               contentCode: r.target.result,
    //               uid: file.uid,
    //               width: img.width,
    //               height: img.height,
    //             },
    //             file,
    //           });
    //         };
    //       } else {
    //         resolve({
    //           type: "media",
    //           data: file,
    //           file,
    //         });
    //       }
    //     };
    //   });
    // });
    // Promise.all(filePromises).then((results) => {
    //   const encodePhotos = [];
    //   const mediaFiles = [];
    //   results.forEach((result) => {
    //     if (result.type === "image") {
    //       encodePhotos.push(result.data);
    //     } else {
    //       mediaFiles.push(result.data);
    //     }
    //   });
    //   const updatedFiles = [...(formData?.files || []), ...clipboardFiles];
    //   const updatedMediaFiles = [
    //     ...(formData?.mediaFiles || []),
    //     ...mediaFiles,
    //   ];
    //   const updatedEncodePhotos = [
    //     ...(formData?.encodePhotos || []),
    //     ...encodePhotos,
    //   ];
    //   setFormData({
    //     text,
    //     files: updatedFiles,
    //     mediaFiles: updatedMediaFiles,
    //     encodePhotos: updatedEncodePhotos,
    //   });
    //   setUploadFiles([...uploadFiles, ...(mediaFiles || [])]);
    // });
  };

  const extractSingleLink = async (text) => {
    // const urlRegex = /https?:\/\/[^\s]+/g;
    // const matches = text.match(urlRegex);
    // if (matches?.length === 1) {
    //   await fetchLink(matches[0]);
    // } else {
    //   setFormData({
    //     ...formData,
    //     previewLink: {
    //       image: null,
    //       title: null,
    //       description: null,
    //       url: null,
    //     },
    //   });
    // }
  };

  const handleAddEmoji = (emojiKey) => {
    if (inputSendRef?.current?.insertEmoji) {
      inputSendRef.current.focus();
      inputSendRef.current.insertEmoji(emojiKey);
    }
  };

  const handleSendSticker = async (sticker) => {
    const userIds =
      user?.userId === friend?.userId
        ? [user?.userId]
        : [user?.userId, friend?.userId];

    const result = await apiFactory.conversationApi.createGroup({
      name: "",
      userIds: userIds,
      conversationType: conversationType.PERSONAL,
    });

    const payload = {
      conversationId: result?.data?.conversationId,
      requestUuid: uuidv4(),
      stickerId: sticker.stickerId,
      content: sticker.resourceId,
      token: Cookies.get("access_token"),
      contentType: "STICKER",
      frameCount: sticker.frameCount,
    };

    await apiFactory.messageApi.sendMessage(payload);
    navigate(`/conversation/chat/${result?.data?.conversationId}`);
  };

  const getContainer = () => {
    return messageRef?.current || document.querySelector(".message-detail");
  };

  useEffect(() => {
    const container = getContainer();
    if (!container) {
      return;
    }

    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        let changed = false;

        entries.forEach((entry) => {
          const el = entry.target;
          if (!el || !(el instanceof HTMLElement)) return;

          if (entry.isIntersecting) {
            if (el.dataset.isUnread === "1") {
              el.dataset.isUnread = "0";
              changed = true;
              try {
                observerRef.current.unobserve(el);
              } catch (e) {}
            }
          }
        });

        if (changed) {
          const containerNow = getContainer();
          const readCount = containerNow
            ? containerNow.querySelectorAll('.message-item[data-is-unread="0"]')
                .length
            : 0;
          setUnreadInDOM(readCount);
        }
      },
      {
        root: container,
        threshold: 0.1,
      },
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, []);

  return (
    <div className=" overflow-hidden flex flex-row flex-1">
      <div className="w-full">
        <DndContext>
          <div
            className="chat-content h-[100vh]"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <div
              className={`header border-b-[1px] border-[#ddd] shadow-md z-10000
                 ${pinnedMessages?.length > 0 && "mb-[50px]"}
                `}
              ref={headerRef}
            >
              <div className="left-chat">
                <button
                  className="btn-back-to-conversation-list"
                  onClick={onBackToConversations}
                >
                  <LeftOutlined />
                </button>
                <MessageAvatar person={friend} styleCss={{}} />

                <div>
                  <div className="text-[#000] text-[16px] font-bold">
                    {friend?.name}
                  </div>
                  <ShowTimeOffline
                    personalDetail={friend}
                    languageMap={languageMap}
                    user={user}
                  />
                </div>
              </div>

              <div className="right">
                <Tooltip
                  placement="bottom"
                  title={
                    languageMap?.["menu.conversation.cancel"] ??
                    "Cancel conversation"
                  }
                  color={"#0091ff"}
                >
                  <Button
                    className="bg-[#2a56b9] text-[white]"
                    onClick={() => navigate("/conversation")}
                    icon={<CloseOutlined className="text-[18px]" />}
                  />
                </Tooltip>
              </div>
            </div>

            <div
              className="bg-[#EAEEF0] overflow-y-scroll border-none outline-none message-detail w-[100%] px-[10px]"
              ref={messageRef}
              tabIndex={0}
              onFocus={() => setKeydownMenuOn(false)}
              onBlur={() => setKeydownMenuOn(true)}
              onScroll={onScrollMessageList}
              onClick={() => setShowSticker(false)}
            >
              {user &&
                messageList?.map((message, index) => {
                  const isUnreadInit =
                    selectedConversation?.lastSeenTs < message.createdAt
                      ? "1"
                      : "0";

                  if (index === 0) {
                    //add more shadow message
                    return <div key={"shadow-item"} className="h-[100px]" />;
                  }

                  return (
                    <div
                      key={message?.requestUuid}
                      className="message-item"
                      data-request-uuid={message.requestUuid}
                      ref={(el) => {
                        if (el) {
                          messageMapRef.current[message?.requestUuid] = el;

                          el.dataset.isUnread =
                            el.dataset.isUnread ?? isUnreadInit;

                          if (
                            observerRef.current &&
                            el.dataset.isUnread === "1"
                          ) {
                            try {
                              observerRef.current.observe(el);
                            } catch (e) {}
                          }
                        }
                      }}
                    >
                      <GenerateMessageContent
                        isNotShowAvatar={isNotShowAvatar(
                          index > 0 ? messageList[index - 1] : {},
                          message,
                        )}
                        isNotShowTime={isNotShowTime(
                          messageList?.[index + 1] || {},
                          message,
                        )}
                        message={message}
                        fileStore={fileStore}
                        selectedConversation={selectedConversation}
                        setQuotation={setQuotation}
                        jumpToMessage={jumpToMessage}
                        viewTargetMessage={viewTargetMessage}
                        inputSendRef={inputSendRef}
                      />
                    </div>
                  );
                })}
            </div>
            <div
              className="bg-[#ffffff] input relative !p-[0] z-[101]"
              style={{
                borderTopLeftRadius:
                  Object.entries(typingUsers)?.length > 0 ? "unset" : "",
              }}
            >
              <div className="w-[100%] flex flex-col">
                <div className="border-y-[1px] border-[#ddd] px-[10px] py-[5px] gap-2 flex">
                  <StickerTable
                    onStickerSend={handleSendSticker}
                    onEmojiSelect={handleAddEmoji}
                  />

                  <FileSending formData={formData} setFormData={setFormData} />

                  <Tooltip
                    placement="top"
                    title={languageMap?.["modal.conversation.vote"] ?? "Vote"}
                    color={"#0091ff"}
                  >
                    <Button
                      className="bg-[#2a56b9] text-[white]"
                      icon={<MdOutlineHowToVote className="text-[18px]" />}
                      onClick={() => setIsOpenModalVote(true)}
                    />
                  </Tooltip>

                  <Tooltip
                    placement="top"
                    title={
                      languageMap?.["modal.conversation.schedule"] ?? "Schedule"
                    }
                    color={"#0091ff"}
                  >
                    <Button
                      className="bg-[#2a56b9] text-[white]"
                      icon={<LuAlarmClock className="text-[18px]" />}
                      onClick={() => setIsChatScheduleModal(true)}
                    />
                  </Tooltip>
                </div>

                <QuotationResponse
                  quotation={quotation}
                  setQuotation={setQuotation}
                />
                {formData?.previewLink?.image && (
                  <ChatPreviewLink
                    previewLink={formData?.previewLink}
                    removePreviewLink={removePreviewLink}
                  />
                )}

                <div className="flex w-[100%] items-center">
                  {inputValue &&
                    showSticker &&
                    stickerSuggestions.length > 0 && (
                      <div className="sticker-suggestions">
                        {stickerSuggestions.map((item) => (
                          <div
                            className="sticker-item-wrapper"
                            key={item.stickerId}
                            onClick={(e) => {
                              e.preventDefault();
                              setShowSticker(false);
                              handleSendSticker(item);
                              setInputValue("");
                            }}
                          >
                            <StickerItem
                              resourceId={item.resourceId}
                              frameCount={item.frameCount}
                              size={60}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  <ContentEditableRichInput
                    value={inputValue}
                    onChange={(event) => {
                      setInputValue(event.target.value);
                      extractSingleLink(event.target.value);
                    }}
                    placeholder={
                      languageMap?.["messageList.input.placeholder"] ??
                      "Message"
                    }
                    ref={inputSendRef}
                    onPaste={handleFileChange}
                    onKeyDown={onKeyDown}
                    onClick={() =>
                      onChangeUnreadConversation(
                        conversationList?.find(
                          (conversation) =>
                            conversation.conversationId ===
                            selectedConversation.conversationId,
                        ),
                        conversationList,
                        handleUpdateConversationList,
                      )
                    }
                    setStickerSuggestions={setStickerSuggestions}
                    setShowSticker={setShowSticker}
                    stickerKeywords={stickerKeywords}
                  />
                  <Button
                    className="bg-[#2a56b9] text-[white] mr-[10px]"
                    onClick={sendMessage}
                    icon={<FiSend size={16} />}
                  />
                </div>

                <FilesPreview
                  formData={formData}
                  setFormData={setFormData}
                  isOnlyView={true}
                />
              </div>
            </div>
          </div>
        </DndContext>

        {isChatScheduleModal && (
          <ChatScheduleModal
            onCancel={() => {
              setIsChatScheduleModal(false);
              setScheduleMessage(null);
            }}
            open={isChatScheduleModal}
            onConfirm={() => {}}
            friendId={friend?.userId}
          />
        )}

        {isOpenModalVote && (
          <CreateVoteModal
            isOpen={isOpenModalVote}
            onClose={() => setIsOpenModalVote(false)}
            friendId={friend?.userId}
          />
        )}
      </div>
    </div>
  );
};

export { VirtualRoom };

